import { useEffect, useRef, useState } from 'react';
import { accounts, type AuthProblem } from '../backend';
import { promptDialog } from '../confirm';
import { SENDER } from '../mail';
import { linkSentHint } from '../people';
import { signInTarget } from '../invite';
import { captchaOn } from '../captcha';
import { companyInAddress } from '../company';
import { COPYRIGHT } from '../brand';
import Turnstile from './Turnstile';
import Logo from './Logo';

/* Whose software this is, quiet at the foot of the sign-in screen — below the
   card on every state (form, emailed-link redeem, link-sent), so it is the one
   thing a visitor always sees before they are in. */
const copyrightFoot = <p className="login-copyright">{COPYRIGHT}</p>;

/*
 * WHICH COMPANY THE ADDRESS NAMES, SHOWN ON THE CARD — #245.
 *
 * `app.flightdeck.build/trinre/` should say TRINRE before a password is typed;
 * the address bar is not where anyone is looking while signing in. The name is
 * read through `company_at()` (0066) — a security-definer lookup that returns
 * the name and nothing else, and was built for exactly this disclosure. So a
 * visitor who guesses `/acme/` is told that company exists; that was weighed
 * when mockup A was chosen over B, and accepted.
 *
 * The mark is initials in the same tile the app draws for a person, because no
 * company logo is stored anywhere — the name is the whole of it.
 */
const companyInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

/*
 * KEYED ON WHAT WENT WRONG, not on the provider's own string.
 *
 * These used to be Firebase's codes — `auth/invalid-email` and the rest. That
 * works exactly until the provider changes, at which point every message
 * quietly becomes the fallback and the sign-in screen stops explaining
 * anything. src/backend/auth.ts turns a provider's code into one of these.
 */
const ERROR_MESSAGES: Record<AuthProblem, string> = {
  'bad-email': 'That does not look like a valid email address.',
  /*
   * TURNED AWAY, IN PLAIN WORDS — Vinnie's instruction, 11 August.
   *
   * It said "No account with that email address", which is accurate and leaves
   * somebody standing there. There is no way to sign yourself up, so the ONLY
   * route in is an administrator adding you: saying so is the difference
   * between a dead end and an instruction.
   */
  'no-account':
    'That address has no access to FlightDeck. Accounts are created by an administrator — ask yours to add you.',
  'wrong-password': 'That email and password do not match. Use the emailed link instead.',
  'too-many': 'Too many attempts — wait a minute and try again.',
  'link-expired': 'That link has already been used or has expired. Ask for a new one.',
  // Every test copy gets its own web address, and a new one has to be trusted
  // before it can sign anybody in. The raw code said nothing.
  'untrusted-address':
    'Sign-in is not available at this address. Please contact your administrator.',
  'needs-recent-sign-in': 'For your safety this needs a fresh sign-in. Sign in again and retry.',
  'weak-password': 'That password is too easily guessed. Try a longer one.',
  unknown: '',
};

/** The message for a failure, falling back to whatever the provider said. */
function signInProblem(e: unknown): string {
  const why = e as { reason?: AuthProblem; code?: string };
  return (why.reason && ERROR_MESSAGES[why.reason]) || `Could not sign you in — ${why.code || 'unknown error'}.`;
}

const REMEMBER_KEY = 'flightdeck-signin-email';

export default function Login() {
  const [error, setError] = useState('');
  const [sent, setSent] = useState('');
  const [busy, setBusy] = useState<'' | 'link' | 'password'>('');
  const [resetting, setResetting] = useState(false);
  const [reset, setReset] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);
  /*
   * THE CHALLENGE, where there is one. Both pieces of state are dead weight in
   * an environment with no site key — see src/captcha.ts — and that is on
   * purpose: one condition decides whether the whole thing exists, rather than
   * a flag here that can disagree with a flag there.
   *
   * `spentAt` is how the widget is told its token has been used. Cloudflare
   * issues one token per solve and Supabase refuses a second use, so a failed
   * attempt leaves a dead token behind and the NEXT attempt fails for a reason
   * that has nothing to do with what was typed.
   */
  const [token, setToken] = useState('');
  const [spentAt, setSpentAt] = useState(0);

  /*
   * THE COMPANY THE ADDRESS NAMES, resolved once — #245. Empty until the lookup
   * answers, and it stays empty when the address carries no company (the
   * ordinary case) or names one that does not exist, so the card falls back to
   * the plain FlightDeck sign-in in both. The name appearing a beat after the
   * card is deliberate: nothing is held up waiting for it.
   */
  const [company, setCompany] = useState('');
  useEffect(() => {
    const slug = companyInAddress();
    if (!slug) return;
    let live = true;
    void accounts
      .companyAt(slug)
      .then(({ name, here }) => {
        if (live && here && name) setCompany(name);
      })
      .catch(() => {
        /* A lookup that fails just leaves the plain card — no worse than today. */
      });
    return () => {
      live = false;
    };
  }, []);

  /** Hand over the token and throw it away in the same breath. */
  const spend = () => {
    const one = token;
    if (captchaOn()) {
      setToken('');
      setSpentAt((n) => n + 1);
    }
    return one;
  };

  /**
   * Cloudflare has not finished, or could not be reached. Either way there is
   * nothing to send and Supabase would refuse it, so the person is told rather
   * than left watching a button do nothing.
   */
  const notReady = 'The security check has not finished. Give it a moment, or reload the page.';

  /*
   * ── ARRIVING FROM AN EMAILED LINK ──────────────────────────────────────
   *
   * NOTHING IS REDEEMED UNTIL A PERSON PRESSES SOMETHING, and that is the whole
   * point of this screen.
   *
   * It used to redeem on load. The emailed link points here carrying a token,
   * so a plain fetch of it consumes nothing — but anything that RUNS the page
   * consumed it, and company mail systems run every link they deliver to check
   * it for phishing. On 18 August 2026 three people at TRINRE were signed in by
   * their employer's scanner within a minute of being invited: Supabase minted a
   * real session for each, the trail recorded a sign-in none of them made, and
   * the link — single use — was spent before they ever clicked it. One of them
   * had to be invited twice. All three sessions came from the same browser
   * build, which is what gave it away.
   *
   * A scanner opens pages. It does not press buttons. So the token is held and
   * the redemption waits for a press, which is the one thing that distinguishes
   * a person from a check.
   */
  /*
   * READ DURING THE FIRST RENDER, not in an effect. An effect runs after the
   * page is painted, so the ordinary sign-in form appeared and was then
   * replaced — a flash of the wrong screen at the exact moment somebody is
   * deciding whether the link they clicked is trustworthy.
   */
  const [linkToRedeem, setLinkToRedeem] = useState(() =>
    typeof window !== 'undefined' && accounts.isSignInLink(window.location.href)
      ? window.location.href
      : ''
  );

  useEffect(() => {
    if (!linkToRedeem) return;
    /*
     * TAKEN OUT OF THE ADDRESS BAR IMMEDIATELY, while it is still unspent. It
     * is a credential: it does not belong in history, in a bookmark, or in
     * whatever a browser syncs between machines. The copy above is what gets
     * redeemed.
     */
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const redeemLink = async () => {
    const stored = localStorage.getItem(REMEMBER_KEY);
    /*
     * Only ask where the answer is actually needed. Firebase checks the address
     * against the link; Supabase carries it inside the link, and asking anyway
     * met every invited person with a browser prompt before they had seen the
     * product — on a link that already knew who they were.
     */
    let email = stored || '';
    if (accounts.linkNeedsEmail && !email) {
      const typed = await promptDialog({
        title: 'Confirm your email',
        body: 'Enter the email address this sign-in link was sent to, so we can confirm it’s you.',
        label: 'Email address',
        placeholder: 'you@organisation.com',
        required: true,
        submitLabel: 'Confirm',
      });
      if (typed === null) return; // backed out — no sign-in, no error
      email = typed.trim();
    }
    if (accounts.linkNeedsEmail && !email) {
      setError('Enter the email address the link was sent to.');
      return;
    }
    setBusy('link');
    setError('');
    try {
      await accounts.completeSignInLink(email.trim(), linkToRedeem);
      localStorage.removeItem(REMEMBER_KEY);
    } catch (e) {
      /*
       * A SPENT LINK IS A DEAD END UNLESS THIS SCREEN OFFERS A WAY ON, so the
       * token is dropped and the ordinary sign-in screen — with its two ways
       * out — takes over, under the message saying what went wrong.
       */
      setError(signInProblem(e));
      setLinkToRedeem('');
    } finally {
      setBusy('');
    }
  };

  const emailValue = () => (emailRef.current?.value || '').trim();

  const sendLink = async () => {
    const em = emailValue();
    if (!em) return setError('Enter your email address.');
    if (!em.includes('@') || !em.includes('.'))
      return setError('That does not look like an email address.');
    if (captchaOn() && !token) return setError(notReady);
    setBusy('link');
    setError('');
    try {
      await accounts.sendSignInLink(em, signInTarget(), spend());
      localStorage.setItem(REMEMBER_KEY, em);
      setSent(em);
    } catch (e) {
      setError(signInProblem(e));
    } finally {
      setBusy('');
    }
  };

  const signInWithPassword = async () => {
    const em = emailValue();
    const pw = passRef.current?.value || '';
    if (!em) return setError('Enter your email address.');
    if (!pw) return setError('Enter your password.');
    if (captchaOn() && !token) return setError(notReady);
    setBusy('password');
    setError('');
    try {
      await accounts.signInWithPassword(em, pw, spend());
    } catch (e) {
      setError(signInProblem(e));
    } finally {
      setBusy('');
    }
  };

  if (sent) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <div className="brand login-brand">
            <Logo tile={34} mark={21} />
          </div>
          <div className="login-explain">
            {/*
              THIS SCREEN IS NOW ONLY EVER REACHED BY SOMEBODY WHO HAS AN
              ACCOUNT, so it says so plainly.

              It used to hedge — "IF there is an account for you, a link is on
              its way" — because saying "no such account" turns a sign-in box
              into a way of asking whether somebody works here, one address at a
              time. Changed on Vinnie's instruction, 11 August: an unknown
              address is now refused outright before reaching this screen, so
              hedging here would only puzzle the person who really is expecting
              an email. The reasoning for and against is in sendSignInLink.
            */}
            <strong>Check your email.</strong> A sign-in link is on its way to{' '}
            <strong>{sent}</strong>. Open it on this device and you are in.
          </div>
          {/* One string rather than text-then-expression: written as two, JSX
              strips the newline between them and it read "The sender
              isnoreply@…" — on the one screen somebody looks at when the email
              has not arrived, where the address is the thing they need. */}
          <p className="hint">{linkSentHint(SENDER)}</p>
          <button className="btn" onClick={() => setSent('')}>
            Use a different email
          </button>
        </div>
        {copyrightFoot}
      </div>
    );
  }

  /*
   * THE ONE PRESS THAT FINISHES IT.
   *
   * Deliberately the whole screen rather than a banner above the sign-in form:
   * somebody who has just clicked a link in an email is not looking for a form,
   * and offering one beside the button is offering a choice where there is only
   * one sensible move. The ways out appear if this fails, and not before.
   */
  if (linkToRedeem) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <div className="brand login-brand">
            <Logo tile={34} mark={21} />
          </div>
          <div className="login-explain">
            <strong>Press to finish signing in.</strong>
          </div>
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy !== ''}
            onClick={() => void redeemLink()}
          >
            {busy === 'link' ? 'Signing you in…' : 'Sign me in'}
          </button>
          {error && <div className="login-error">{error}</div>}
          {/*
            NO EXPLANATION ON THE SCREEN. It carried a paragraph about company
            mail systems opening links — the reason the press exists, written
            for whoever inherits this file and put in front of the person
            instead. Vinnie, 18 August: "All they want to know is that they can
            log in, they don't need to know all that."

            The reason is at the top of this component, where the person who
            might delete the press will read it. What is left here is one
            instruction and the button that carries it out.
          */}
        </div>
        {copyrightFoot}
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <form
        className="login-card"
        onSubmit={(e) => {
          e.preventDefault();
          void signInWithPassword();
        }}
      >
        {/* #245 — when the address names a company, the card LEADS with it: the
            company mark and name at the top, and the FlightDeck mark moves to a
            quiet footer at the foot of the card (below) so the two marks do not
            clash. With no company it is the plain card — FlightDeck at the top,
            as before. */}
        {company ? (
          <div className="co-head">
            <div className="co-mark" aria-hidden="true">
              {companyInitials(company)}
            </div>
            <div className="co-head-text">
              <div className="co-name">{company}</div>
              <div className="co-role">Signing in to this company</div>
            </div>
          </div>
        ) : (
          <>
            <div className="brand login-brand">
              <Logo tile={34} mark={21} />
            </div>
            <div className="login-explain">
              Sign in with your <strong>email and password</strong>.
            </div>
          </>
        )}

        <label className="stack">
          Email
          <input
            ref={emailRef}
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            defaultValue=""
            onInput={() => setError('')}
          />
        </label>

        <label className="stack">
          Password
          <input
            ref={passRef}
            className="input"
            type="password"
            name="password"
            autoComplete="current-password"
            defaultValue=""
            onInput={() => setError('')}
          />
        </label>

        {/* Above the error, so a challenge that is still thinking is visible
            next to the sentence saying so. */}
        <Turnstile onToken={setToken} resetAt={spentAt} />

        {error && <div className="login-error">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={busy !== ''}>
          {busy === 'password' ? 'Signing in…' : 'Sign in'}
        </button>

        {/* #245 — a way off a company that is not yours: drop it from the address
            and sign in on the plain one. (Signing in here still lands you in your
            own company regardless — the address is read, never trusted — but the
            header should not keep naming someone else's.) */}
        {company && (
          <p className="hint login-not-this">
            Not {company}?{' '}
            <button
              type="button"
              className="link"
              onClick={() => window.location.assign('/')}
            >
              Sign in without it
            </button>
          </p>
        )}

        {/*
          THE TWO WAYS OUT, TOGETHER, UNDER THE QUESTION THEY ANSWER.
          
          They were two links stacked one above the other, reading almost the
          same — and the first of them was a MODE rather than an action: you
          pressed "I already have a password", the screen rearranged itself,
          and then you typed. Vinnie, 18 August: "It's 1 step too many."

          So the password is the screen now, and pressing the link SENDS the
          link rather than switching to a screen where you can ask for one.
          Both of these answer one problem — no password, or one you cannot
          remember — so they sit under that question rather than beside the
          sign-in button competing with it.
        */}
        <div className="login-escape">
          <p className="hint">No password, or cannot remember it?</p>
          <button
            className="link login-alt"
            type="button"
            disabled={busy !== '' || resetting}
            onClick={() => void sendLink()}
          >
            {busy === 'link' ? 'Sending…' : 'Email me a sign-in link'}
          </button>
          <span className="login-escape-or" aria-hidden="true">
            or
          </span>
          {/*
            It says the same thing whether the address is known or not, for the
            reason on Accounts.sendPasswordReset: a screen that says "no such
            account" is a way of asking whether somebody works here.
          */}
          <button
            className="link login-alt"
            type="button"
            disabled={busy !== '' || resetting}
            onClick={() => {
              const em = emailValue();
              if (!em.includes('@')) {
                setError('Enter your email address first, and I will send a link to set a new password.');
                return;
              }
              if (captchaOn() && !token) {
                setError(notReady);
                return;
              }
              setError('');
              setResetting(true);
              void accounts
                .sendPasswordReset(em, window.location.origin, spend())
                .then(
                  () => setReset(em),
                  (e: unknown) => setError(signInProblem(e))
                )
                .finally(() => setResetting(false));
            }}
          >
            {resetting ? 'Sending…' : 'Set a new password'}
          </button>
        </div>

        {reset && (
          <p className="hint">
            If there is an account for <strong>{reset}</strong>, a link to set a new password is on
            its way. It comes from {SENDER}.
          </p>
        )}

        {/* #245 — FlightDeck's mark lives here when a company leads the card:
            present, but quiet, and clear of the company at the top. */}
        {company && (
          <div className="brand login-brand-foot">
            <Logo tile={25} mark={15} />
          </div>
        )}
      </form>
      {copyrightFoot}
    </div>
  );
}
